import { EditContent, EditPolygon } from "@/pages/editor";
import { HotSpot, PixelCoordinate } from "@xnav/types/src/common";
import { addPointToPolygon, createNewPolygon, drawEditablePolygon, drawHiddenPolygon, drawPolygon, handlePolygonDrag, handlePolygonPointDrag, handlePotentialPolygonClicks, scalePolygons, createPreMadeShape, handlePolygonScale, handlePolygonRotate } from "./polygonFunctions";
import { MarkupTool } from "@/components/videoMarkupEditor";
import { createNewHotSpot, drawHotspots, handleHotspotDrag, handlePotentialHotspotClick, scaleHotSpots } from "./hotspotFunctions";

export function calcMidPoint(p1: PixelCoordinate, p2: PixelCoordinate): PixelCoordinate {
	return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
}

export function getClientMousePosition(event: MouseEvent): PixelCoordinate {
	const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
	return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

export function clickingOnBox(click: PixelCoordinate, boxTopLeft: PixelCoordinate, boxWidthHeight: PixelCoordinate): boolean {
	return click.x >= boxTopLeft.x && click.x <= boxTopLeft.x + boxWidthHeight.x && click.y >= boxTopLeft.y && click.y <= boxTopLeft.y + boxWidthHeight.y;
}

export function clickingOnCircle(click: PixelCoordinate, circleCenter: PixelCoordinate, radius: number): boolean {
	return Math.sqrt((click.x - circleCenter.x) ** 2 + (click.y - circleCenter.y) ** 2) <= radius;
}

export function drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string, strokeColor?: string) {
	ctx.beginPath();
	ctx.arc(x, y, radius, 0, 2 * Math.PI);
	ctx.fillStyle = color;
	ctx.fill();
	if (strokeColor) {
		ctx.strokeStyle = strokeColor;
		ctx.stroke();
	}
	ctx.closePath();
}

export function handleCanvasClick(
	event: MouseEvent,
	content: EditContent,
	setContent: (content: EditContent) => void,
	tool: MarkupTool,
	time: number,
	setTool?: (tool: MarkupTool) => void
) {
	const click = getClientMousePosition(event);
	let tempContent = content;
	if (content.selectedMarkup) {
		const targetMarkup = content.selectedMarkup.type === 'polygon'
			? content.polygons[content.selectedMarkup.index]
			: content.hotSpots[content.selectedMarkup.index];

		if (!targetMarkup) {
			console.log(`[CANVAS CLICK TRACE] Hard resetting stale selectedMarkup index.`);
			tempContent.selectedMarkup = undefined;
		} else {
			const isOutsideTime: boolean = content.selectedMarkup.type === 'polygon' ?
				(targetMarkup as EditPolygon).start > time || (targetMarkup as EditPolygon).end < time :
				(targetMarkup as HotSpot).timeStamps[0] > time || (targetMarkup as HotSpot).timeStamps[(targetMarkup as HotSpot).timeStamps.length - 1] < time;

			if (isOutsideTime) {
				console.log(`[CANVAS CLICK TRACE] Deselecting markup due to Out of Time boundaries!`);
				tempContent.selectedMarkup = undefined;
			}
		}
	}
	if (handlePotentialPolygonClicks(click, time, tempContent, setContent, tool)) {
		console.log(`[CANVAS CLICK TRACE] Intercepted by existing Polygon ray-caster body block! ABORTING POINT ADDITION.`);
		return;
	}
	if (handlePotentialHotspotClick(click, time, tempContent, setContent)) {
		console.log(`[CANVAS CLICK TRACE] Intercepted by Hotspot logic! ABORTING POINT ADDITION.`);
		return;
	}

	console.log(`[CANVAS CLICK TRACE] sucessfully bypassed geometry interceptors. Processing ${tool} action!`);
	if (tool === 'polygon') {
		if (!tempContent.selectedMarkup) setContent({
			...tempContent,
			selectedMarkup: {
				type: 'polygon',
				index: tempContent.polygons.length,
				subIndex: 0,
			},
			polygons: [...tempContent.polygons, createNewPolygon(click, time)]
		})
		else setContent({
			...tempContent,
			selectedMarkup: {
				...tempContent.selectedMarkup,
				subIndex: tempContent.selectedMarkup.subIndex + 1,
			},
			polygons: tempContent.polygons.map((polygon, index) => index === tempContent.selectedMarkup!.index ? addPointToPolygon(polygon as EditPolygon, click) : polygon as EditPolygon),
		})
	} else if (tool === 'triangle' || tool === 'rectangle' || tool === 'pentagon' || tool === 'arrow' || tool === 'line' || tool === 'circle' || tool === 'ellipse' || tool === 'text') {
		const newShape = createPreMadeShape(tool, click, time);
		setContent({
			...tempContent,
			selectedMarkup: {
				type: 'polygon',
				index: tempContent.polygons.length,
				subIndex: -1,
			},
			polygons: [...tempContent.polygons, newShape]
		});
		if (setTool) setTool('pointer');
	} else if (tool === 'hotspot') {
		setContent({
			...tempContent,
			selectedMarkup: {
				type: 'hotspot',
				index: tempContent.hotSpots.length,
				subIndex: 0,
			},
			hotSpots: [...tempContent.hotSpots, createNewHotSpot(click, time)]
		})
	}
	else {
		setContent({
			...tempContent,
			selectedMarkup: undefined,
		})
	}
}

export function handleCanvasDrag(event: MouseEvent, lastMousePosition: PixelCoordinate, content: EditContent, setContent: (content: EditContent) => void) {
	if (!content.selectedMarkup) return
	const click = getClientMousePosition(event);
	if (content.selectedMarkup.type === 'polygon') {
		if (content.selectedMarkup.subIndex === -2) handlePolygonScale(click, lastMousePosition, content, setContent, event.shiftKey)
		else if (content.selectedMarkup.subIndex === -3) handlePolygonRotate(click, lastMousePosition, content, setContent)
		else if (content.selectedMarkup.subIndex !== -1) handlePolygonPointDrag(click, content, setContent)
		else handlePolygonDrag(click, lastMousePosition, content, setContent)
	}
	if (content.selectedMarkup.type === 'hotspot') handleHotspotDrag(click, content, setContent)
}

export function isFrameHidden(hiddenFrames: (string | number)[] | undefined, time: number): boolean {
	if (!hiddenFrames || hiddenFrames.length === 0) return false;
	return hiddenFrames.some(t => {
		if (typeof t === 'number') {
			return t === time;
		}
		if (typeof t === 'string') {
			const clean = t.trim();
			if (clean.includes('-') || clean.toLowerCase().includes('to')) {
				const parts = clean.split(/[-]|to/i);
				if (parts.length === 2) {
					const start = parseFloat(parts[0]);
					const end = parseFloat(parts[1]);
					if (!isNaN(start) && !isNaN(end)) {
						return time >= start && time <= end;
					}
				}
			}
			const val = parseFloat(clean);
			return !isNaN(val) && val === time;
		}
		return false;
	});
}

export function drawContents(ctx: CanvasRenderingContext2D, content: EditContent, time: number) {
	ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
	content.polygons.forEach((polygon, index) => {
		if (time < polygon.start || time > polygon.end) return
		if (isFrameHidden(polygon.hiddenFrames as any, time)) {
			drawHiddenPolygon(ctx, polygon as EditPolygon)
		}
		else if (content.selectedMarkup?.type === 'polygon' && content.selectedMarkup.index === index) {
			drawEditablePolygon(ctx, polygon as EditPolygon)
		}
		else drawPolygon(ctx, polygon as EditPolygon)
	})
	drawHotspots(time, content, ctx)
}

export function scaleMarkup(scale: number = 1, content: EditContent): EditContent {
	if (!content) return content;
	if (scale < 0) return content;
	return {
		...content,
		polygons: scalePolygons(content.polygons || [], scale),
		hotSpots: scaleHotSpots(content.hotSpots || [], scale)
	};
} 
